import {
  passkeysSupported,
  type PortalMe,
  type PortalSession,
  type TrustedBrowser,
} from '@central/api';
import {
  Card,
  PageHeader,
  RecoveryCodeList,
  StatusChip,
  color,
  describeUserAgent,
  formatAgo,
  formatDate,
  shownError,
  useReauth,
  InfoRow,
  ChangePasswordDialog,
} from '@central/ui';
import { Alert, Button, Loader, Modal, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { api, credentialsQuery, meQuery, sessionsQuery, trustedBrowsersQuery } from '../api';
import { NotificationPrefs } from '../components/NotificationPrefs';
import { OrganizationSettings } from '../components/OrganizationSettings';
import { PasskeySetup, TotpSetup } from '../components/SecondFactorSetup';
import { errorMessage } from '../format';
import { InviteModal, People } from './TeamPeople';

const RECOVERY_SET = 10;

function ErrorAlert({ error }: { error: unknown }) {
  const e = shownError(error);
  return e ? <Alert color="red">{errorMessage(e)}</Alert> : null;
}

/**
 * Team & security (docs/design/CustomerTeam.dc.html): the organization's
 * people and invites, then the signed-in user's second factors, recovery
 * codes, password, sessions, trusted browsers and email notifications, then
 * the organization's settings, full width. Removing a passkey, creating codes, adding a factor
 * beside an existing one and changing the password need a second factor
 * proved in the last 10 minutes (useReauth).
 */
export function Team() {
  const ctx = useRouteContext({ from: '/app' });
  // The route context holds the me loaded at navigation; factor changes update the query.
  const { data: me = ctx.me } = useQuery(meQuery);
  const isOwner = me.user.role === 'Owner';
  const [inviting, setInviting] = useState(false);

  return (
    <>
      <PageHeader
        title="Team & security"
        subtitle={[
          'Owners invite people and set roles.',
          me.customer.requireMfa
            ? 'A second factor is required for everyone in your organization.'
            : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        actions={isOwner && <Button onClick={() => setInviting(true)}>Invite someone</Button>}
      />
      <People meId={me.user.id} isOwner={isOwner} />
      <InviteModal opened={inviting} onClose={() => setInviting(false)} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 380px', maxWidth: 640, minWidth: 0 }}>
          <YourSignIn me={me} />
        </div>
        <div style={{ flex: '1 1 380px', maxWidth: 640, minWidth: 0 }}>
          <SignedIn />
        </div>
        <div style={{ flex: '1 1 380px', maxWidth: 640, minWidth: 0 }}>
          <NotificationPrefs />
        </div>
      </div>
      <OrganizationSettings me={me} />
    </>
  );
}

function YourSignIn({ me }: { me: PortalMe }) {
  const qc = useQueryClient();
  const f = me.user.secondFactors;
  const { guard, prompt } = useReauth(f, { api: api.auth, describeError: errorMessage });
  const [adding, setAdding] = useState<'passkey' | 'totp'>();
  const [codes, setCodes] = useState<string[]>();
  const [changingPassword, setChangingPassword] = useState(false);
  const passkeys = useQuery(credentialsQuery);
  const hasFactor = f.totp || f.passkeys > 0;

  const done = async (next: PortalMe) => {
    qc.setQueryData(meQuery.queryKey, next);
    setAdding(undefined);
    await qc.invalidateQueries({ queryKey: credentialsQuery.queryKey });
  };
  const remove = useMutation({
    mutationFn: (id: string) => guard(() => api.auth.deleteCredential(id)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQuery.queryKey });
      await qc.invalidateQueries({ queryKey: credentialsQuery.queryKey });
      // Removing a passkey forgets every trusted browser.
      await qc.invalidateQueries({ queryKey: trustedBrowsersQuery.queryKey });
    },
  });
  const generate = useMutation({
    mutationFn: () => guard(() => api.auth.generateRecoveryCodes()),
    onSuccess: async (r) => {
      setCodes(r.codes);
      await qc.invalidateQueries({ queryKey: meQuery.queryKey });
    },
  });

  return (
    <Card title="Your sign-in">
      <InfoRow label="Email">{me.user.email}</InfoRow>
      <InfoRow label="Role">{me.user.role}</InfoRow>
      <InfoRow label="Authenticator app">
        {f.totp ? (
          <StatusChip tone="ok">On</StatusChip>
        ) : (
          <>
            <StatusChip tone="unk">Off</StatusChip>
            {adding !== 'totp' && (
              <Button variant="subtle" size="compact-sm" ml={8} onClick={() => setAdding('totp')}>
                Set up
              </Button>
            )}
          </>
        )}
      </InfoRow>
      {adding === 'totp' && (
        <div style={{ padding: '12px 0' }}>
          <TotpSetup onDone={done} guard={guard} />
        </div>
      )}
      <InfoRow label="Recovery codes">
        {f.recoveryCodes} of {RECOVERY_SET} left
        <Button
          variant="subtle"
          size="compact-sm"
          ml={8}
          disabled={!hasFactor}
          loading={generate.isPending}
          onClick={() => generate.mutate()}
        >
          {f.recoveryCodes > 0 ? 'Regenerate' : 'Create'}
        </Button>
      </InfoRow>
      <ErrorAlert error={generate.error} />
      <div style={{ padding: '12px 0 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: color.textMuted }}>Passkeys</span>
          {adding !== 'passkey' && passkeysSupported() && (
            <Button variant="subtle" size="compact-sm" onClick={() => setAdding('passkey')}>
              Add
            </Button>
          )}
        </div>
        {adding === 'passkey' && (
          <div style={{ padding: '12px 0' }}>
            <PasskeySetup onDone={done} guard={guard} />
          </div>
        )}
        {passkeys.error ? (
          <ErrorAlert error={passkeys.error} />
        ) : !passkeys.data ? (
          <Loader size="sm" aria-label="Loading" />
        ) : passkeys.data.items.length === 0 ? (
          <p style={{ margin: '8px 0 0', fontSize: 14, color: color.textMuted }}>None</p>
        ) : (
          <ul aria-label="Passkeys" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {passkeys.data.items.map((p) => (
              <ListRow
                key={p.id}
                main={p.name}
                detail={`Added ${formatDate(p.createdAt)} · ${
                  p.lastUsedAt ? `last used ${formatAgo(p.lastUsedAt)}` : 'never used'
                }`}
                action={
                  <Button
                    variant="subtle"
                    color="red"
                    size="compact-sm"
                    aria-label={`Remove ${p.name}`}
                    loading={remove.isPending && remove.variables === p.id}
                    onClick={() => remove.mutate(p.id)}
                  >
                    Remove
                  </Button>
                }
              />
            ))}
          </ul>
        )}
        <ErrorAlert error={remove.error} />
      </div>
      <InfoRow label="Password">
        {me.user.hasPassword ? (
          <>
            {me.user.passwordChangedAt ? `Changed ${formatDate(me.user.passwordChangedAt)}` : 'Set'}
            <Button
              variant="subtle"
              size="compact-sm"
              ml={8}
              onClick={() => setChangingPassword(true)}
            >
              Change
            </Button>
          </>
        ) : (
          <span style={{ color: color.textMuted }}>None · you sign in with single sign-on</span>
        )}
      </InfoRow>
      {!hasFactor && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: color.textMuted }}>
          Add a second factor so a stolen password alone can’t open your account.
        </p>
      )}
      <ChangePasswordDialog
        opened={changingPassword}
        guard={guard}
        onClose={() => setChangingPassword(false)}
        changePassword={(b) => api.auth.changePassword(b)}
        onChanged={() =>
          Promise.all(
            [meQuery, sessionsQuery, trustedBrowsersQuery].map((q) =>
              qc.invalidateQueries({ queryKey: q.queryKey }),
            ),
          )
        }
      />
      {prompt}
      <Modal opened={!!codes} onClose={() => setCodes(undefined)} title="Your new recovery codes">
        <Stack gap={12}>
          <Text size="sm">
            Each code signs you in once. They are shown only once, and your earlier codes no longer
            work.
          </Text>
          {codes && <RecoveryCodeList codes={codes} />}
        </Stack>
      </Modal>
    </Card>
  );
}

/**
 * "Where you're signed in": the user's sessions, each of which can be signed
 * out, and the browsers trusted to skip the second step.
 */
function SignedIn() {
  const qc = useQueryClient();
  const sessions = useQuery(sessionsQuery);
  const refreshSessions = () => qc.invalidateQueries({ queryKey: sessionsQuery.queryKey });
  const signOut = useMutation({
    mutationFn: (id: string) => api.sessions.revoke(id),
    onSuccess: refreshSessions,
  });
  const others = useMutation({
    mutationFn: () => api.sessions.signOutOthers(),
    onSuccess: refreshSessions,
  });
  const list = useQuery(trustedBrowsersQuery);
  const forget = useMutation({
    mutationFn: (id: string) => api.auth.revokeTrustedBrowser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: trustedBrowsersQuery.queryKey }),
  });
  const hasOthers = sessions.data?.items.some((s) => !s.current);
  return (
    <Card
      title="Where you’re signed in"
      action={
        hasOthers && (
          <Button
            variant="subtle"
            size="compact-sm"
            loading={others.isPending}
            onClick={() => others.mutate()}
          >
            Sign out everywhere else
          </Button>
        )
      }
    >
      {sessions.error ? (
        <ErrorAlert error={sessions.error} />
      ) : !sessions.data ? (
        <Loader size="sm" aria-label="Loading" />
      ) : (
        <ul aria-label="Sessions" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {sessions.data.items.map((s) => (
            <SessionRow
              key={s.id}
              s={s}
              busy={signOut.isPending && signOut.variables === s.id}
              onSignOut={() => signOut.mutate(s.id)}
            />
          ))}
        </ul>
      )}
      <ErrorAlert error={signOut.error ?? others.error} />
      <h3 style={{ margin: '18px 0 6px', fontSize: 14 }}>Trusted browsers</h3>
      {list.error ? (
        <ErrorAlert error={list.error} />
      ) : !list.data ? (
        <Loader size="sm" aria-label="Loading" />
      ) : list.data.items.length === 0 ? (
        <p style={{ margin: '8px 0 0', fontSize: 14, color: color.textMuted }}>
          No trusted browsers. Tick “Trust this browser for 7 days” when you sign in.
        </p>
      ) : (
        <ul aria-label="Trusted browsers" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {list.data.items.map((b) => (
            <BrowserRow
              key={b.id}
              b={b}
              busy={forget.isPending && forget.variables === b.id}
              onForget={() => forget.mutate(b.id)}
            />
          ))}
        </ul>
      )}
      <ErrorAlert error={forget.error} />
    </Card>
  );
}

function SessionRow({
  s,
  busy,
  onSignOut,
}: {
  s: PortalSession;
  busy: boolean;
  onSignOut: () => void;
}) {
  const name = describeUserAgent(s.userAgent ?? '');
  return (
    <ListRow
      main={name}
      detail={[s.ip, `signed in ${formatDate(s.createdAt)}`, `active ${formatAgo(s.lastSeenAt)}`]
        .filter(Boolean)
        .join(' · ')}
      action={
        s.current ? (
          <StatusChip tone="ok">This browser</StatusChip>
        ) : (
          <Button
            variant="subtle"
            color="red"
            size="compact-sm"
            aria-label={`Sign out ${name}`}
            loading={busy}
            onClick={onSignOut}
          >
            Sign out
          </Button>
        )
      }
    />
  );
}

function BrowserRow({
  b,
  busy,
  onForget,
}: {
  b: TrustedBrowser;
  busy: boolean;
  onForget: () => void;
}) {
  const name = describeUserAgent(b.userAgent);
  return (
    <ListRow
      main={
        <>
          {name} {b.current && <StatusChip tone="ok">This browser</StatusChip>}
        </>
      }
      detail={[
        b.ip,
        `trusted ${formatDate(b.createdAt)}`,
        b.lastUsedAt ? `last used ${formatAgo(b.lastUsedAt)}` : undefined,
        `until ${formatDate(b.expiresAt)}`,
      ]
        .filter(Boolean)
        .join(' · ')}
      action={
        <Button
          variant="subtle"
          color="red"
          size="compact-sm"
          aria-label={`Forget ${name}${b.current ? ' (this browser)' : ''}`}
          loading={busy}
          onClick={onForget}
        >
          Forget
        </Button>
      }
    />
  );
}

function ListRow({ main, detail, action }: { main: ReactNode; detail: string; action: ReactNode }) {
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '9px 0',
        borderBottom: `1px solid ${color.rowRule}`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 14 }}>{main}</span>
        <span style={{ fontSize: 13, color: color.textMuted }}>{detail}</span>
      </div>
      {action}
    </li>
  );
}
